import { ReactElement, useEffect, useMemo, useState } from "react";
import {
  Box,
  styled,
  Paper,
  Stack,
  Typography,
  Button,
  Avatar,
  Chip,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Badge,
  Tooltip,
  useTheme,
  Zoom,
  alpha,
  lighten,
  darken,
} from "@mui/material";

import type { GridColDef } from "@mui/x-data-grid";

import "dayjs/locale/pt-br";
import api from "../../../services/api";

import { useOutletContext } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { MoreVert, QueryBuilder, Search, Verified } from "@mui/icons-material";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ptBR as ptBRDatagrid } from "@mui/x-data-grid/locales";

import { ReactComponent as IconUserNotFound } from "../../../assets/icons/userNotFound.svg";
import useStyles from "./styles";
import MenuAdm from "../../../componentes/administracao/menu";
import Filters from "../../../componentes/administracao/filters";
import NewAlertModal from "../../../componentes/newAlertModal";
import { useNotification } from "../../../componentes/notificationProvider";
import MobileTable from "../../../componentes/mobileTable";
import { maskCnpj, maskCPF, removeMask } from "../../../utils/maskFields";
import ModalCreateUser from "./modalCreateUser";
import ModalEditProfile from "./modalEditProfile";
import ModalEditUserPassword from "./modalEditUserPassword";
import ModalEditPermission from "./modalEditPermission";
import {
  RowsData,
  StatusOption,
} from "../../../componentes/administracao/types";
import { AxiosError, AxiosResponse } from "axios";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { socket } from "../../../services/socket";
import getColorsForScheme from "../../../componentes/legends/utils/getColorsForScheme";

const StyledBadge = styled(Badge)(({ theme, color }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: color,
    color: color,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: "1px solid currentColor",
      content: '""',
    },
  },
}));

const permissoes = [
  { name: "Admin", value: "adm002" },
  { name: "Desenvolvedor", value: "adm001" },
  { name: "Usuário", value: "user001" },
];

type OutletContextType = {
  setSubTitle: (subtitle: string) => void;
  settings: RowsData[];
};

export default function Administracao() {
  const { setSubTitle, settings } = useOutletContext<OutletContextType>();
  const [loadingPage, setLoadingPage] = useState(false);
  const [role, setRole] = useState("");
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState<RowsData[]>(settings);
  const [pagePermission, setPagePermission] = useState<[]>([]);
  const [openEditProfileModal, setOpenEditProfileModal] = useState(false);
  const [openCreateUserModal, setOpenCreateUserModal] = useState(false);
  const [openEditLoginModal, setOpenEditLoginModal] = useState(false);
  const [openEditPermissionModal, setOpenEditPermissionModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<RowsData | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [verifyStatus, setVerifyStatus] = useState<StatusOption>({
    value: "",
    label: "",
  });
  const [permissionStatus, setPermissionStatus] = useState<StatusOption>({
    value: "",
    label: "",
  });

  const [modalAlert, setModalAlert] = useState(false);
  const columnVisibilityMobile: Record<string, boolean> = {
    foto: false,
    options: false,
    nome: true,
    sobrenome: true,
    nomeFantasia: true,
    email: true,
    cpf_cnpj: true,
    login: true,
    verificado: true,
    codigoPermissao: true,
    RlPageUserPermission: true,
    ultimoAcesso: true,
    createAt: true,
  };

  const theme = useTheme();
  const colorMode = theme.palette.mode;
  const styles = useStyles(theme, colorMode);
  const notify = useNotification();
  useEffect(() => {
    setSubTitle("Administração");
  }, [setSubTitle]);

  useEffect(() => {
    setSelectedRow(
      (row) => (row?.id && rows.find((r) => r.id === row.id)) || null,
    );
  }, [rows]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      updateStatusOnline();
    }, 10000);

    return () => window.clearInterval(interval);
  }, []);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>, row: RowsData) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNewOpen = () => {
    setOpenCreateUserModal(true);
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchText(event.target.value);
  };

  const getFilteredRows = useMemo(() => {
    return rows?.filter((row: RowsData) => {
      return (
        (searchText
          ? row?.PerfilCliente?.nomeFantasia
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            row?.PerfilCliente?.nome
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            row?.PerfilCliente?.sobrenome
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            row?.email?.toLowerCase().includes(searchText.toLowerCase()) ||
            (removeMask(row?.PerfilCliente?.cpf).includes(
              removeMask(searchText),
            ) &&
              !!removeMask(searchText)) ||
            (removeMask(row?.PerfilCliente?.cnpj).includes(
              removeMask(searchText),
            ) &&
              !!removeMask(searchText))
          : true) &&
        row.verificado?.toString().includes(verifyStatus?.value?.toString()) &&
        row.codigoPermissao?.includes(String(permissionStatus?.value))
      );
    });
  }, [verifyStatus, permissionStatus, rows, searchText]);

  useEffect(() => {
    setRole(
      JSON.parse(String(localStorage.getItem("1"))).usuarioCliente
        .codigoPermissao,
    );
    async function apiGetPagePermission() {
      try {
        setLoadingPage(true);
        const response = await api.get("/admin/pages/findMany");
        setPagePermission(response.data);
      } catch (error) {
        console.error(error);
        if (error instanceof AxiosError)
          notify(error.status || 500, undefined, "error", error);
        else notify("Não foi possível se conectar ao servidor!", "error");
      } finally {
        setLoadingPage(false);
      }
    }
    setRows(settings);
    apiGetPagePermission();
  }, [settings]);

  async function updateStatusOnline() {
    try {
      socket.emit("onlineUsers", {}, (response: AxiosResponse) => {
        if (response.status !== 200) {
        } else {
          let responseData = response?.data;

          setRows((elements) => {
            return elements.map((element) => {
              const user = responseData.find(
                (user: RowsData) => user.id === element.id,
              );

              if (user) {
                element.online = true;
              } else {
                element.online = false;
              }
              return element;
            });
          });
        }
      });
    } catch (error) {
      console.error(error);
      if (error instanceof AxiosError)
        notify(error?.status || 500, undefined, "error", error);
      else console.error(error);

      notify("Não foi possivel atualizar status de conectividade!", "error");
    }
  }

  const columns: GridColDef<RowsData>[] = [
    {
      sortable: false,
      field: "foto",
      headerName: "",
      width: 60,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent={"center"}
            height="100%"
          >
            <Tooltip
              title={params.row?.online ? "online" : "offline"}
              arrow
              slots={{
                transition: Zoom,
              }}
            >
              <StyledBadge
                hidden={!params.row?.online}
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                variant="dot"
                sx={{
                  height: "fit-content",
                }}
                color={
                  params.row?.online
                    ? (theme.palette.success.main as "primary")
                    : (theme.palette.error.main as "error")
                }
              >
                <Avatar
                  alt={
                    params?.row?.PerfilCliente?.nome
                      ? params?.row?.PerfilCliente?.nome
                      : params?.row?.PerfilCliente?.nomeFantasia
                  }
                  src={params?.row?.PerfilCliente?.foto || "/"}
                  sx={styles.foto}
                />
              </StyledBadge>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      minWidth: 200,
      flex: 1,
      field: "email",
      headerName: "E-mail",
      display: "flex",
    },
    {
      minWidth: 150,
      flex: 1,
      field: "nome",
      headerName: "Nome",
      display: "flex",
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack
          // sx={{
          //   display: "flex",
          //   flexDirection: "column",
          //   justifyContent: "center",
          //   alignItems: "start",
          // }}
          >
            {params?.row?.PerfilCliente?.nome || "-"}
          </Stack>
        );
      },
    },
    {
      minWidth: 100,
      flex: 1,
      field: "sobrenome",
      headerName: "Sobrenome",
      display: "flex",
      renderCell: (params: GridRenderCellParams) => {
        return <Stack>{params?.row?.PerfilCliente?.sobrenome || "-"}</Stack>;
      },
    },
    {
      minWidth: 100,
      flex: 1,
      field: "nomeFantasia",
      headerName: "Nome Fantasia",
      display: "flex",
      renderCell: (params: GridRenderCellParams) => {
        return <Stack>{params?.row?.PerfilCliente?.nomeFantasia || "-"}</Stack>;
      },
    },
    {
      minWidth: 150,
      flex: 1,
      field: "cpf_cnpj",
      headerName: "CPF/CNPJ",
      display: "flex",
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack>
            {maskCPF(params?.row?.PerfilCliente?.cpf) ||
              maskCnpj(params?.row?.PerfilCliente?.cnpj) ||
              "-"}
          </Stack>
        );
      },
    },
    {
      field: "verificado",
      headerName: "Verificado",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          color={params.value === true ? "success.main" : "text.secondary"}
          sx={styles.containerColumnVerificado}
        >
          <Icon sx={styles.iconVerified}>
            {params.value === true ? <Verified /> : <QueryBuilder />}
          </Icon>
          <Typography fontSize={13} fontWeight={500}>
            {params.value === true ? "Ativo" : "Aguardando"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "codigoPermissao",
      headerName: "Permissão",
      minWidth: 130,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack sx={styles.containerColumnPermissao} spacing={1}>
            <Chip
              label={
                permissoes.find((perm) => perm.value === params?.value)?.name
              }
              color="primary"
              sx={
                params?.value === "adm001"
                  ? styles.chipPermSuperAdmin
                  : params?.value === "adm002"
                    ? styles.chipPermAdmin
                    : params?.value === "user001"
                      ? styles.chipPermUsuario
                      : styles.chipPermDesenvolvedor
              }
              variant="filled"
            />
          </Stack>
        );
      },
    },
    {
      field: "RlPageUserPermission",
      headerName: "Permissão de páginas",
      headerAlign: "center",
      width: 260,
      renderCell: (params: GridRenderCellParams) => {
        const exibiPagePermissions = pagePermission.filter(
          (page: { id: string; title: string }) =>
            params.value.some(
              (permission: { pageId: string }) => permission.pageId === page.id,
            ),
        ) as { id: string; title: string }[];

        const colors = getColorsForScheme(
          "category10",
          exibiPagePermissions.length,
        );

        return (
          <Stack sx={styles.containerColumnPermissao}>
            {params.row.codigoPermissao === "adm001" ? (
              <Chip
                key={"todas"}
                label={"Todas"}
                sx={{
                  backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                  color: theme.palette.text.primary,
                }}
                variant="filled"
              />
            ) : (
              exibiPagePermissions.map((page, index) => (
                <Chip
                  key={page.id}
                  label={page?.title}
                  color="primary"
                  sx={{
                    backgroundColor: alpha(colors[index], 0.2),
                    color:
                      colorMode === "dark"
                        ? lighten(colors[index], 0.5)
                        : darken(colors[index], 0.5),
                  }}
                  variant="filled"
                />
              ))
            )}
          </Stack>
        );
      },
    },
    {
      sortable: false,
      field: "ultimoAcesso",
      headerName: "Último acesso",
      width: 120,
      display: "flex",
      renderCell: (params: GridRenderCellParams) => {
        return (
          params.value && (
            <Stack sx={styles.containerColumnLastLogin}>
              <Typography fontSize={13} sx={styles.textDate}>
                {format(String(params.value), "d 'de' LLL, yyy", {
                  locale: ptBR,
                })}
              </Typography>
              <Typography fontSize={12} sx={styles.subTextDate}>
                {format(String(params.value), "'às' HH:mm:ss", {
                  locale: ptBR,
                })}
              </Typography>
            </Stack>
          )
        );
      },
    },
    {
      sortable: false,
      field: "createAt",
      headerName: "Criado em",
      width: 120,

      renderCell: (params: GridRenderCellParams) => {
        return (
          params.value && (
            <Stack sx={styles.containerColumnCreatedAt} spacing={1}>
              <Typography fontSize={13} sx={styles.textDate}>
                {format(String(params.value), "d 'de' LLL, yyy", {
                  locale: ptBR,
                })}
              </Typography>
            </Stack>
          )
        );
      },
    },
    {
      sortable: false,
      field: "options",
      headerName: "Opções",
      width: 70,
      renderCell: (params: GridRenderCellParams) => {
        const disabledButton =
          JSON.parse(String(localStorage.getItem("1"))).usuarioCliente?.id ===
            params?.row?.id ||
          (JSON.parse(String(localStorage.getItem("1"))).usuarioCliente
            ?.codigoPermissao !== "adm001" &&
            params?.row?.codigoPermissao === "adm001");

        return (
          <IconButton
            disabled={disabledButton}
            onClick={(e) => handleClick(e, params.row)}
            size="large"
          >
            <MoreVert fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxNoRowsOverlay}>
        <IconUserNotFound style={styles.iconUserNotFound} />
        <Typography color="textDisabled">{`Não possui usuários `}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={styles.containerBox}>
        <Paper elevation={0} sx={styles.paper}>
          <Box sx={styles.boxHeader}>
            <Box sx={styles.boxSearchAndFilter}>
              <TextField
                size="small"
                label="Pesquisar Usuário"
                variant="filled"
                sx={styles.searchInput}
                value={searchText}
                onChange={handleSearchInputChange}
                autoComplete="off"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Filters
                verifyStatus={verifyStatus}
                setVerifyStatus={setVerifyStatus}
                permissionStatus={permissionStatus}
                setPermissionStatus={setPermissionStatus}
                handleNewOpen={handleNewOpen}
              />
              <Tooltip title="Adicionar usuário">
                <Button
                  onClick={handleNewOpen}
                  sx={styles.buttonMobile}
                  variant="contained"
                >
                  <AddIcon />
                </Button>
              </Tooltip>
            </Box>
          </Box>

          <Box width="100%">
            <MobileTable
              autoHeight={true}
              getRowHeight={() => "auto"}
              localeText={
                ptBRDatagrid.components.MuiDataGrid.defaultProps.localeText
              }
              loading={loadingPage}
              disableColumnMenu
              disableColumnFilter
              hideFooterPagination
              hideFooterSelectedRowCount
              hideFooter
              sx={styles.dataGrid}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
              rows={getFilteredRows}
              columns={columns}
              onOptionsClick={(
                e: React.MouseEvent<HTMLElement>,
                row: RowsData,
              ) => handleClick(e, row)}
              columnVisibility={columnVisibilityMobile}
              noRowsOverlay={CustomNoRowsOverlay}
              headerMobile={({ email, PerfilCliente, online }: RowsData) => (
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                  <StyledBadge
                    hidden={online}
                    //badgeContent={online ? 0 : ""}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    variant="dot"
                    color={
                      online
                        ? ("#44b700" as "primary")
                        : (theme.palette.error.main as "error")
                    }
                    sx={{ height: "fit-content" }}
                  >
                    <Avatar
                      sizes={"35px"}
                      alt={
                        PerfilCliente?.nome
                          ? PerfilCliente?.nome
                          : PerfilCliente?.nomeFantasia
                      }
                      src={PerfilCliente?.foto || "/"}
                    />
                  </StyledBadge>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      {email}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Box>
        </Paper>
      </Box>

      {anchorEl && (
        <MenuAdm
          open={open}
          anchorEl={anchorEl}
          handleClose={handleClose}
          setOpenEditProfileModal={setOpenEditProfileModal}
          setOpenEditLoginModal={setOpenEditLoginModal}
          setOpenEditPermissionModal={setOpenEditPermissionModal}
          setModalAlert={setModalAlert}
        />
      )}
      <ModalEditProfile
        openEditProfileModal={openEditProfileModal}
        setOpenEditProfileModal={setOpenEditProfileModal}
        selectedRow={selectedRow as RowsData}
        rows={rows as RowsData[]}
        setRows={setRows}
      />
      <ModalCreateUser
        openCreateUserModal={openCreateUserModal}
        setOpenCreateUserModal={setOpenCreateUserModal}
        rows={rows as RowsData[]}
        setRows={setRows}
        selectedRow={selectedRow as RowsData}
        permissoes={permissoes as any}
        role={role}
        pagePermission={pagePermission}
      />

      <ModalEditUserPassword
        openEditLoginModal={openEditLoginModal}
        setOpenEditLoginModal={setOpenEditLoginModal}
        selectedRow={selectedRow}
      />
      <ModalEditPermission
        openEditPermissionModal={openEditPermissionModal}
        setOpenEditPermissionModal={setOpenEditPermissionModal}
        selectedRow={selectedRow}
        pagePermission={pagePermission}
        rows={rows as RowsData[]}
        setRows={setRows}
        role={role}
      />

      <NewAlertModal
        openModal={modalAlert}
        setOpenModal={setModalAlert}
        textContent={
          "Se você prosseguir com a operação o usuário será desativado"
        }
        handleButton={() => {
          //handleApiPutstatus(String(selectedRow?.id), !selectedRow?.status);
          setModalAlert(false);
        }}
        severity={"warning"}
        singleButton={false}
      />
    </>
  );
}
